import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { BrandApi } from "@/api/brand.api";
import { RedemptionMerchantApi } from "@/api/redemptionMerchant.api";
import { PlatformCategoryApi } from "@/api/platformCategory.api";
import { ProgramApi } from "@/api/program.api";
import { ProductApi } from "@/api/product.api";
import { toast } from "sonner";

// Import components
import { CategoryTree } from "@/components/program-categories/CategoryTree";
import { CategoryFormDialog } from "@/components/program-categories/CategoryFormDialog";
import { AssignProductDialog } from "@/components/program-categories/AssignProductDialog";
import { ProductAssignmentPanel } from "@/components/program-categories/ProductAssignmentPanel";

// Import utilities
import {
  flattenCategories,
  findAndUpdateCategory,
  findAndRemoveCategory,
  insertCategory,
  isDescendant,
  findParentId,
  mapApiCategoryToLocal,
} from "@/components/program-categories/utils";

// Constants
const PLACEHOLDER_IMAGE = "/placeholder.svg";

const ProgramCategories = () => {
  const [searchParams] = useSearchParams();
  const programId = searchParams.get("programId") || "73"; // Default to 73 if not provided

  const [categories, setCategories] = useState([]);
  const [programName, setProgramName] = useState("");
  const [programLoading, setProgramLoading] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [merchantFilter, setMerchantFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(true);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [categoryProductsLoading, setCategoryProductsLoading] = useState(false);
  const [userSelectedCategory, setUserSelectedCategory] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  const [assignProductDialogOpen, setAssignProductDialogOpen] = useState(false);
  const [assignProductSearch, setAssignProductSearch] = useState("");
  const [assignProductMerchant, setAssignProductMerchant] = useState("");
  const [assignProductBrand, setAssignProductBrand] = useState("");
  const [assignProductCode, setAssignProductCode] = useState("");
  const [assignProductPage, setAssignProductPage] = useState(0);
  const [assignProductPageSize, setAssignProductPageSize] = useState(5);
  const [assignProductTotal, setAssignProductTotal] = useState(0);
  const [assignProductLoading, setAssignProductLoading] = useState(false);
  const [assignProductRows, setAssignProductRows] = useState([]);
  const [assignProductSelected, setAssignProductSelected] = useState([]);
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showCategoryFormDialog, setShowCategoryFormDialog] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    categoryName: "",
    categoryCode: "",
    categoryDescription: "",
  });
  const [categoryLogo, setCategoryLogo] = useState(null);
  const [categoryLogoPreview, setCategoryLogoPreview] = useState(null);
  const [actionType, setActionType] = useState("");
  const [selectedCategoryForAction, setSelectedCategoryForAction] =
    useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Find selected brand and merchant objects for filter mapping
  const selectedBrand = brands.find((b) => b.id === Number(brandFilter));
  const selectedMerchant = merchants.find(
    (m) => m.id === Number(merchantFilter)
  );

  useEffect(() => {
    async function fetchProgramDetails() {
      setProgramLoading(true);
      try {
        const { data } = await ProgramApi.fetchProgram({
          programId: programId,
          pageNumber: 0,
          pageSize: 1,
        });
        if (data?.programVOs && data.programVOs.length > 0) {
          setProgramName(data.programVOs[0].programName);
        } else {
          setProgramName(`Program ${programId}`);
        }
      } catch (error) {
        console.error("Error fetching program details:", error);
        setProgramName(`Program ${programId}`);
      } finally {
        setProgramLoading(false);
      }
    }

    async function fetchCategories() {
      setLoading(true);
      try {
        const { data } = await PlatformCategoryApi.getProgramCategoryTree(
          programId,
          "ROOT"
        );
        console.log("Program Categories API Response:", data);
        if (data) {
          // Create a structure that includes the ROOT category with its sub-categories
          const rootCategory = mapApiCategoryToLocal(data);
          console.log("Mapped Categories with ROOT:", rootCategory);
          setCategories([rootCategory]);
        } else {
          setCategories([]);
        }
      } catch {
        setCategories([]);
      } finally {
        setLoading(false);
      }
    }
    fetchProgramDetails();
    fetchCategories();
  }, [programId]);

  // Debug: Log categories whenever they change
  useEffect(() => {
    console.log("Categories state updated:", categories);
  }, [categories]);

  useEffect(() => {
    async function fetchBrands() {
      setBrandsLoading(true);
      try {
        const { data } = await BrandApi.fetchAllBrands();
        setBrands(
          (data || []).map((b) => ({
            value: b.id,
            label: b.brandName,
            id: b.id,
            name: b.brandName,
          }))
        );
      } catch {
        setBrands([]);
      } finally {
        setBrandsLoading(false);
      }
    }
    fetchBrands();
  }, []);

  useEffect(() => {
    async function fetchMerchants() {
      setMerchantsLoading(true);
      try {
        const { data } =
          await RedemptionMerchantApi.fetchAllRedemptionMerchants();
        setMerchants(
          (data?.redemptionMerchantVOS || []).map((m) => ({
            value: m.id,
            label: m.merchantName,
            id: m.id,
            name: m.merchantName,
          }))
        );
      } catch {
        setMerchants([]);
      } finally {
        setMerchantsLoading(false);
      }
    }
    fetchMerchants();
  }, []);

  useEffect(() => {
    // Only fetch if both filters are selected or searchText is not empty
    if (!(selectedBrand && selectedMerchant) && !searchText) {
      setProducts([]);
      setProductsLoading(false);
      return;
    }
    async function fetchProducts() {
      setProductsLoading(true);
      try {
        const { data } = await ProductApi.fetchGiftCardProducts({
          brandId: null,
          productName: searchText || "",
          productCode: null,
          countryId: null,
          redemptionMerchantId: null,
          platformCategoryId: null,
          programCategoryId: selectedCategoryId,
          productType: "GIFT_CARD",
          pageNumber: 0,
          sortBy: "createdDate",
          sortOrder: "desc",
          pageSize: 5,
        });
        setProducts(
          (data?.giftCardProductVOs || []).map((p) => ({
            id: p.id,
            name: p.productName,
            brand: p.brandVO?.brandName || "",
            merchant: p.redemptionMerchantVO?.merchantName || "",
            shippedFrom: p.redemptionMerchantVO?.countryVO?.name || "",
            image: p.productImageUri || "/placeholder.svg",
            categoryId:
              (p.platformCategoryIds && p.platformCategoryIds[0]) || "",
          }))
        );
      } catch {
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    }
    fetchProducts();
  }, [brandFilter, merchantFilter, searchText]);

  useEffect(() => {
    if (!selectedCategoryId || !userSelectedCategory) {
      setCategoryProducts([]);
      setCategoryProductsLoading(false);
      setTotalProducts(0);
      return;
    }
    setCategoryProductsLoading(true);
    ProductApi.fetchGiftCardProducts({
      brandId: null,
      productName: searchText || "",
      productCode: null,
      countryId: null,
      redemptionMerchantId: null,
      platformCategoryId: null,
      programCategoryId: selectedCategoryId,
      productType: "GIFT_CARD",
      pageNumber: currentPage,
      sortBy: "createdDate",
      sortOrder: "desc",
      pageSize: pageSize,
    })
      .then(({ data }) => {
        setCategoryProducts(
          (data?.giftCardProductVOs || []).map((p) => ({
            id: p.id,
            name: p.productName,
            brand: p.brandVO?.brandName || "",
            merchant: p.redemptionMerchantVO?.merchantName || "",
            shippedFrom: p.redemptionMerchantVO?.countryVO?.name || "",
            image: p.productImageUri || "/placeholder.svg",
            categoryId:
              (p.platformCategoryIds && p.platformCategoryIds[0]) || "",
            programCategoryAssignmentDataVOs:
              p.programCategoryAssignmentDataVOs || [],
          }))
        );
        setTotalProducts(data?.totalRecords || 0);
      })
      .catch((error) => {
        console.error("Error fetching category products:", error);
        setCategoryProducts([]);
        setTotalProducts(0);
      })
      .finally(() => setCategoryProductsLoading(false));
  }, [
    selectedCategoryId,
    brandFilter,
    merchantFilter,
    searchText,
    userSelectedCategory,
    currentPage,
    pageSize,
  ]);

  useEffect(() => {
    if (!assignProductDialogOpen) return;
    setAssignProductLoading(true);
    ProductApi.fetchGiftCardProducts({
      brandId: null,
      productName: assignProductSearch || "",
      productCode: null,
      countryId: null,
      redemptionMerchantId: null,
      platformCategoryId: null,
      programCategoryId: null,
      productType: "GIFT_CARD",
      pageNumber: assignProductPage,
      sortBy: "createdDate",
      sortOrder: "desc",
      pageSize: assignProductPageSize,
    })
      .then(({ data }) => {
        setAssignProductRows(data?.giftCardProductVOs || []);
        setAssignProductTotal(data?.totalRecords || 0);
      })
      .catch((error) => {
        console.error("Error fetching assign products:", error);
        setAssignProductRows([]);
        setAssignProductTotal(0);
      })
      .finally(() => setAssignProductLoading(false));
  }, [
    assignProductDialogOpen,
    assignProductSearch,
    assignProductMerchant,
    assignProductBrand,
    assignProductCode,
    assignProductPage,
    assignProductPageSize,
  ]);

  const handleCategoryFormAction = (action, category = null) => {
    setActionType(action);
    setSelectedCategoryForAction(category);

    // Only pre-fill form data for edit operations
    if (action === "Edit Category") {
      setCategoryFormData({
        categoryName: category?.name || "",
        categoryCode: category?.categoryCode || "",
        categoryDescription: category?.categoryDescription || "",
      });
      // Set preview for existing category image
      setCategoryLogoPreview(category?.categoryLogoUri || null);
    } else {
      // Clear form for add operations
      setCategoryFormData({
        categoryName: "",
        categoryCode: "",
        categoryDescription: "",
      });
      setCategoryLogoPreview(null);
    }

    setCategoryLogo(null);
    setShowCategoryFormDialog(true);
  };

  const handleCategoryFormSubmit = async () => {
    if (!categoryFormData.categoryName || !categoryFormData.categoryCode) {
      toast.error("Category name and code are required");
      return;
    }

    setIsSubmitting(true);
    try {
      const category = selectedCategoryForAction;
      let updatedFormData = {};

      if (actionType === "Edit Category") {
        updatedFormData = {
          categoryCode: categoryFormData.categoryCode,
          categoryName: categoryFormData.categoryName,
          categoryDescription: categoryFormData.categoryDescription,
          parentCategoryId: category?.parentCategoryId || null,
          subCategoryVOs: [],
          programId: category?.programId || Number(programId),
          id: selectedCategoryForAction?.id, // Use the category ID from selectedCategoryForAction
        };
      } else {
        updatedFormData = {
          categoryCode: categoryFormData.categoryCode,
          categoryName: categoryFormData.categoryName,
          categoryDescription: categoryFormData.categoryDescription,
          parentCategoryId:
            actionType === "Add Sub-Category" ? category?.id : null,
          subCategoryVOs: [],
          programId: category?.programId || Number(programId),
        };
      }

      const formData = new FormData();

      // programCategoryLogo
      if (categoryLogo) {
        formData.append("programCategoryLogo", categoryLogo);
      }

      const blob = new Blob([JSON.stringify(updatedFormData)], {
        type: "application/json",
      });
      formData.append("programCategoryVO", blob);

      await PlatformCategoryApi.createOrUpdateProgramCategory(formData, {
        contentType: "multipart/form-data",
      });

      toast.success(
        actionType === "Edit Category"
          ? "Category updated successfully"
          : "Category created successfully"
      );
      setShowCategoryFormDialog(false);

      // Refetch categories to update the list
      const { data: categoriesData } =
        await PlatformCategoryApi.getProgramCategoryTree(programId, "ROOT");
      if (categoriesData) {
        const rootCategory = mapApiCategoryToLocal(categoriesData);
        setCategories([rootCategory]);
      }

      // Reset form
      setCategoryFormData({
        categoryName: "",
        categoryCode: "",
        categoryDescription: "",
      });
      setCategoryLogo(null);
      setCategoryLogoPreview(null);
      setActionType("");
      setSelectedCategoryForAction(null);
    } catch (error) {
      console.error("Error creating/updating category:", error);
      toast.error("Failed to create/update category");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCategory = (cat) => {
    setCategoryToDelete(cat);
    setShowDeleteDialog(true);
  };
  const handleToggleHide = (cat) => {
    console.log("handleToggleHide called with category:", cat);
    if (!cat?.id) {
      console.log("No category ID found");
      return;
    }

    // Determine which API to call based on current state
    const isCurrentlyHidden = cat.isHidden || cat.hidden;
    const apiEndpoint = isCurrentlyHidden
      ? `/programCategory/unHideProgramCategory?programCategoryId=${cat.id}`
      : `/programCategory/hideProgramCategory?programCategoryId=${cat.id}`;

    console.log(
      `Making API call to ${isCurrentlyHidden ? "unHide" : "hide"} with ID:`,
      cat.id
    );

    // Use the appropriate API method based on current state
    const apiMethod = isCurrentlyHidden
      ? PlatformCategoryApi.unHideProgramCategory(cat.id)
      : PlatformCategoryApi.hideProgramCategory(cat.id);

    apiMethod
      .then((response) => {
        console.log("API call successful:", response);
        const action = isCurrentlyHidden ? "unhidden" : "hidden";
        toast.success(`Category ${action} successfully`);

        // Refresh categories from API to get updated state
        (async function fetchCategories() {
          setLoading(true);
          try {
            const { data } = await PlatformCategoryApi.getProgramCategoryTree(
              programId,
              "ROOT"
            );
            console.log("Program Categories API Response after hide:", data);
            if (data) {
              // Create a structure that includes the ROOT category with its sub-categories
              const rootCategory = mapApiCategoryToLocal(data);
              console.log(
                "Mapped Categories with ROOT after hide:",
                rootCategory
              );
              setCategories([rootCategory]);
            } else {
              setCategories([]);
            }
          } catch (error) {
            console.error("Error fetching categories:", error);
            setCategories([]);
          } finally {
            setLoading(false);
          }
        })();
      })
      .catch((error) => {
        console.log("API call failed:", error);
        const action = isCurrentlyHidden ? "unhide" : "hide";
        toast.error(`Failed to ${action} category`);
      });
  };

  // Product assignment logic
  const handleRemoveProduct = async (productId) => {
    try {
      // Find the product to get the programCategoryAssignmentId
      const product = categoryProducts.find((p) => p.id === productId);
      if (
        !product ||
        !product.programCategoryAssignmentDataVOs ||
        product.programCategoryAssignmentDataVOs.length === 0
      ) {
        toast.error("No assignment data found for this product");
        return;
      }

      // Get the programCategoryAssignmentId from the assignment data
      console.log(
        "Product assignment data:",
        product.programCategoryAssignmentDataVOs
      );
      console.log(
        "Selected category ID:",
        selectedCategoryId,
        "Type:",
        typeof selectedCategoryId
      );

      // Since we're in a program category context, use the first assignment available
      let assignmentData = product.programCategoryAssignmentDataVOs[0];

      console.log(
        "Product assignment data:",
        product.programCategoryAssignmentDataVOs
      );
      console.log("Using assignment data:", assignmentData);

      if (!assignmentData || !assignmentData.programCategoryAssignmentId) {
        console.log(
          "No assignment data found. Available assignments:",
          product.programCategoryAssignmentDataVOs
        );
        toast.error("No assignment ID found for this product");
        return;
      }

      // Call the unassign API with programCategoryAssignmentId
      await PlatformCategoryApi.unAssignProductsFromProgramCategory([
        assignmentData.programCategoryAssignmentId,
      ]);

      // Refetch category products to update the list
      setCategoryProductsLoading(true);
      try {
        const { data } = await ProductApi.fetchGiftCardProducts({
          brandId: null,
          productName: searchText || "",
          productCode: null,
          countryId: null,
          redemptionMerchantId: null,
          platformCategoryId: null,
          programCategoryId: selectedCategoryId,
          productType: "GIFT_CARD",
          pageNumber: currentPage,
          sortBy: "createdDate",
          sortOrder: "desc",
          pageSize: pageSize,
        });
        setCategoryProducts(
          (data?.giftCardProductVOs || []).map((p) => ({
            id: p.id,
            name: p.productName,
            brand: p.brandVO?.brandName || "",
            merchant: p.redemptionMerchantVO?.merchantName || "",
            shippedFrom: p.redemptionMerchantVO?.countryVO?.name || "",
            image: p.productImageUri || "/placeholder.svg",
            categoryId:
              (p.platformCategoryIds && p.platformCategoryIds[0]) || "",
            programCategoryAssignmentDataVOs:
              p.programCategoryAssignmentDataVOs || [],
          }))
        );
        setTotalProducts(data?.totalRecords || 0);
      } catch (error) {
        console.error("Error refetching category products:", error);
      } finally {
        setCategoryProductsLoading(false);
      }

      toast.success("Product removed from category successfully");
    } catch (error) {
      console.error("Error removing product from category:", error);
      toast.error("Failed to remove product from category");
    }
  };
  const handleMoveProduct = (productId, newCategoryId) => {
    setProducts(
      products.map((p) =>
        p.id === productId ? { ...p, categoryId: newCategoryId } : p
      )
    );
  };

  // Add a reset handler
  const handleResetFilters = () => {
    setBrandFilter("");
    setMerchantFilter("");
    setSearchText("");
    setCurrentPage(0);
    setPageSize(10);
  };

  // Reset handler for assign dialog
  const handleAssignProductReset = () => {
    setAssignProductSearch("");
    setAssignProductMerchant("");
    setAssignProductBrand("");
    setAssignProductCode("");
    setAssignProductPage(0);
    setAssignProductPageSize(5);
    setAssignProductSelected([]);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete?.id) return;
    try {
      // Call the delete API
      await PlatformCategoryApi.deleteProgramCategory(categoryToDelete.id);

      // Remove from local state after successful API call
      const { categories: updatedCategories } = findAndRemoveCategory(
        categories,
        categoryToDelete.id
      );
      setCategories(updatedCategories);
      toast.success("Category deleted successfully");
      setShowDeleteDialog(false);
      setCategoryToDelete(null);
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Failed to delete category");
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Program Categories</h1>
        <p className="text-gray-500">
          {programLoading
            ? "Loading program details..."
            : `Manage categories for ${programName}`}
        </p>
      </div>
      <div className="flex flex-col md:flex-row gap-6 h-full min-h-[700px] items-stretch">
        {/* Category Tree Panel */}
        <Card className="w-full md:w-1/3 max-w-xs min-w-[280px] h-full min-h-[700px]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold">
              Program Categories
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col h-full">
            <Input
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-3"
            />
            <CategoryTree
              categories={categories}
              loading={loading}
              search={search}
              setSearch={setSearch}
              selectedCategoryId={selectedCategoryId}
              setSelectedCategoryId={setSelectedCategoryId}
              setUserSelectedCategory={setUserSelectedCategory}
              handleDeleteCategory={handleDeleteCategory}
              handleToggleHide={handleToggleHide}
              handleCategoryFormAction={handleCategoryFormAction}
            />
          </CardContent>
        </Card>

        {/* Product Assignment Panel */}
        <ProductAssignmentPanel
          selectedCategoryId={selectedCategoryId}
          setSelectedCategoryId={setSelectedCategoryId}
          searchText={searchText}
          setSearchText={setSearchText}
          brandFilter={brandFilter}
          setBrandFilter={setBrandFilter}
          merchantFilter={merchantFilter}
          setMerchantFilter={setMerchantFilter}
          categoryProducts={categoryProducts}
          categoryProductsLoading={categoryProductsLoading}
          totalProducts={totalProducts}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          brands={brands}
          brandsLoading={brandsLoading}
          merchants={merchants}
          merchantsLoading={merchantsLoading}
          categories={categories}
          onAssignProduct={() => {
            if (!selectedCategoryId) {
              toast.error("Please select a category first.");
              return;
            }
            setAssignProductDialogOpen(true);
          }}
          onRemoveProduct={handleRemoveProduct}
          onMoveProduct={handleMoveProduct}
          onResetFilters={handleResetFilters}
          flattenCategories={flattenCategories}
        />

        {/* Assign Product Dialog */}
        <AssignProductDialog
          open={assignProductDialogOpen}
          onOpenChange={setAssignProductDialogOpen}
          assignProductSearch={assignProductSearch}
          setAssignProductSearch={setAssignProductSearch}
          assignProductCode={assignProductCode}
          setAssignProductCode={setAssignProductCode}
          assignProductMerchant={assignProductMerchant}
          setAssignProductMerchant={setAssignProductMerchant}
          assignProductBrand={assignProductBrand}
          setAssignProductBrand={setAssignProductBrand}
          assignProductPage={assignProductPage}
          assignProductPageSize={assignProductPageSize}
          assignProductTotal={assignProductTotal}
          assignProductLoading={assignProductLoading}
          assignProductRows={assignProductRows}
          assignProductSelected={assignProductSelected}
          setAssignProductSelected={setAssignProductSelected}
          showAssignConfirm={showAssignConfirm}
          setShowAssignConfirm={setShowAssignConfirm}
          assigning={assigning}
          merchants={merchants}
          brands={brands}
          selectedCategoryId={selectedCategoryId}
          onAssign={async () => {
            if (!selectedCategoryId) {
              toast.error("Please select a category first.");
              return;
            }
            setAssigning(true);
            try {
              const categoryIdNum = Number(selectedCategoryId);
              if (!categoryIdNum) {
                toast.error("Invalid category selected.");
                setAssigning(false);
                return;
              }
              const payload = assignProductSelected.map((pid) => ({
                programCategoryVO: {
                  id: categoryIdNum,
                  programId: Number(programId),
                },
                giftCardProductVO: { id: pid },
              }));

              // Call the program category assign API
              await PlatformCategoryApi.assignProductsToProgramCategory(
                payload
              );
              // Refetch products for the selected category and update the grid
              setCategoryProductsLoading(true);
              try {
                const { data } = await ProductApi.fetchGiftCardProducts({
                  brandId: null,
                  productName: searchText || "",
                  productCode: null,
                  countryId: null,
                  redemptionMerchantId: null,
                  platformCategoryId: null,
                  programCategoryId: categoryIdNum,
                  productType: "GIFT_CARD",
                  pageNumber: currentPage,
                  sortBy: "createdDate",
                  sortOrder: "desc",
                  pageSize: pageSize,
                });
                setCategoryProducts(
                  (data?.giftCardProductVOs || []).map((p) => ({
                    id: p.id,
                    name: p.productName,
                    brand: p.brandVO?.brandName || "",
                    merchant: p.redemptionMerchantVO?.merchantName || "",
                    shippedFrom: p.redemptionMerchantVO?.countryVO?.name || "",
                    image: p.productImageUri || "/placeholder.svg",
                    categoryId:
                      (p.platformCategoryIds && p.platformCategoryIds[0]) || "",
                    programCategoryAssignmentDataVOs:
                      p.programCategoryAssignmentDataVOs || [],
                  }))
                );
                setTotalProducts(data?.totalRecords || 0);
              } catch {
                setCategoryProducts([]);
                setTotalProducts(0);
              } finally {
                setCategoryProductsLoading(false);
              }
              toast.success("Products assigned successfully");
              setAssignProductDialogOpen(false);
              setAssignProductSelected([]);
              setShowAssignConfirm(false);
            } catch (err) {
              toast.error("Failed to assign products");
            } finally {
              setAssigning(false);
            }
          }}
          onReset={handleAssignProductReset}
          onPageChange={(page) => setAssignProductPage(page)}
          onPageSizeChange={(size) => {
            setAssignProductPageSize(size);
            setAssignProductPage(0);
          }}
        />

        {/* Category Form Dialog */}
        <CategoryFormDialog
          open={showCategoryFormDialog}
          onOpenChange={setShowCategoryFormDialog}
          actionType={actionType}
          categoryFormData={categoryFormData}
          setCategoryFormData={setCategoryFormData}
          categoryLogo={categoryLogo}
          setCategoryLogo={setCategoryLogo}
          categoryLogoPreview={categoryLogoPreview}
          setCategoryLogoPreview={setCategoryLogoPreview}
          isSubmitting={isSubmitting}
          onSubmit={handleCategoryFormSubmit}
          onCancel={() => {
            setShowCategoryFormDialog(false);
            setCategoryFormData({
              categoryName: "",
              categoryCode: "",
              categoryDescription: "",
            });
            setCategoryLogo(null);
            setCategoryLogoPreview(null);
            setActionType("");
            setSelectedCategoryForAction(null);
          }}
        />

        {/* Delete Category Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Category</DialogTitle>
            </DialogHeader>
            <div className="mb-4">
              Are you sure you want to delete the category{" "}
              <b>{categoryToDelete?.name}</b>?
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setCategoryToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDeleteCategory}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ProgramCategories;
